// src/api/entries.ts

import { BASE_URL } from "../utils/config";
import { FeaturedEntry, HoleInOneEntryResponse } from "../types/api";
import { HALL_OF_FAME_LOAD_ERROR_MESSAGE } from "../components/hallOfFame/hallOfFameFormatters";
import { getAuthToken } from "../services/authService";
import {
	EntrySubmissionPayload,
	EntrySubmissionResponse,
} from "../types/entry";

/**
 * Fetches featured recordings.
 * @returns A promise resolving to an array of FeaturedEntry objects.
 * @throws {Error} If the request fails.
 */
export async function fetchFeaturedEntries(count = 5): Promise<FeaturedEntry[]> {
	const url = `${BASE_URL}/entries/featured?count=${count}`;

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error(`Failed to fetch featured entries: ${response.status}`);
		}
		return (await response.json()) as FeaturedEntry[];
	} catch (error: any) {
		console.error("Error fetching featured entries:", error);
		throw new Error(
			error.message || "An error occurred while fetching featured entries.",
		);
	}
}

/**
 * Fetches verified hole-in-one entries for the Hall of Fame.
 */
export async function fetchHoleInOneEntries(): Promise<HoleInOneEntryResponse[]> {
	const url = `${BASE_URL}/entries/hole-in-ones`;

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			console.error(
				"Failed to fetch hole in one entries:",
				response.status,
				response.statusText,
			);
			throw new Error(HALL_OF_FAME_LOAD_ERROR_MESSAGE);
		}
		return await response.json();
	} catch (error: unknown) {
		console.error("Error fetching hole in one entries:", error);
		if (error instanceof Error && error.message === HALL_OF_FAME_LOAD_ERROR_MESSAGE) {
			throw error;
		}
		throw new Error(HALL_OF_FAME_LOAD_ERROR_MESSAGE);
	}
}

/**
 * Creates a new entry for a hole.
 * Requires authentication.
 * @param data - The entry data (teeSelection, entrySelection, hole_slug, group_id)
 * @returns A promise resolving to the entry response with entry_id
 * @throws {Error} If the request fails or user is not authenticated
 */
export async function createEntry(data: {
	teeSelection?: string;
	entrySelection?: string;
	hole_slug: string;
	group_id?: string;
}): Promise<{ entry_id: number }> {
	const url = `${BASE_URL}/entries`;
	const token = getAuthToken();

	if (!token) {
		throw new Error("Authentication required to create entry.");
	}

	try {
		const response = await fetch(url, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(data),
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			const error: any = new Error(
				errorData.error ||
					errorData.message ||
					`Failed to create entry: ${response.status}`,
			);
			error.status = response.status;
			throw error;
		}

		return await response.json();
	} catch (error: any) {
		console.error("Error creating entry:", error);
		if (error.status) {
			throw error;
		}
		throw new Error(error.message || "An error occurred while creating entry.");
	}
}

export class InnerCircleClaimError extends Error {
	constructor(
		message: string,
		public readonly status: number,
		public readonly code?: string,
	) {
		super(message);
	}
}

/**
 * Submits the authenticated user's pending prizes for processing.
 * Email is required only when registering a temporary player.
 */
export async function claimInnerCirclePrize(
	phoneNumber: string,
	email?: string,
): Promise<void> {
	const url = `${BASE_URL}/users/confirm-phone`;
	const token = getAuthToken();

	if (!token) {
		throw new Error("Authentication required to claim prize.");
	}

	const body: { phone_number: string; email?: string } = {
		phone_number: phoneNumber,
	};
	if (email) {
		body.email = email;
	}

	try {
		const response = await fetch(url, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(body),
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			const detail = errorData.detail;
			throw new InnerCircleClaimError(
				errorData.error ||
					errorData.message ||
					(typeof detail === "string" ? detail : detail?.message) ||
					`Failed to confirm phone number: ${response.status}`,
				response.status,
				typeof detail === "object" ? detail?.code : undefined,
			);
		}

		// When temp user submits email, caller (viewRecording) will show success then reload
		// so the user sees confirmation before the page refreshes with the new account.
		return;
	} catch (error: any) {
		console.error("Error confirming phone number:", error);
		if (error.status) {
			throw error;
		}
		throw new Error(
			error.message || "An error occurred while confirming your phone number.",
		);
	}
}
